<template>
  <div class="page-container notice-detail-page">
    <div class="page-header">
      <div class="container">
        <h1>{{ t('pages.notice_detail_title') }}</h1>
        <p class="breadcrumb">
          <router-link to="/">{{ t('index.latest_news') }}</router-link> /
          <router-link to="/notice">{{ t('pages.notice_list_title') }}</router-link> /
          <span>{{ t('pages.notice_detail_title') }}</span>
        </p>
      </div>
    </div>

    <div class="page-content container">
      <el-row :gutter="30">
        <!-- 左侧：公告详情 -->
        <el-col :span="24" :xs="24">
          <div class="detail-wrapper">
            <div v-if="loading" class="loading-placeholder">
              <el-skeleton :rows="10" animated />
            </div>
            <div v-else-if="!noticeDetail" class="empty-data">
              <el-empty :description="t('common.no_data')" />
            </div>
            <div v-else class="notice-article">
              <h2 class="article-title">{{ noticeDetail.title }}</h2>
              <div class="article-meta">
                <span>{{ t('pages.publish_time') }}: {{ noticeDetail.publish_time }}</span>
                <span>{{ t('pages.source') }}: {{ noticeDetail.source }}</span>
                <span>{{ t('pages.author') }}: {{ noticeDetail.author }}</span>
                <span>{{ t('pages.hit_count') }}: {{ noticeDetail.hit_count }}</span>
              </div>
              <div class="article-summary">
                <strong>{{ t('pages.notice_detail_title') }}摘要:</strong> {{ noticeDetail.summary }}
              </div>
              <div class="article-content" v-html="noticeDetail.content">
              </div>

              <div v-if="noticeDetail.file_path" class="pdf-viewer-section">
                <iframe
                  :src="pdfFullUrl"
                  frameborder="0"
                  width="100%"
                  height="600"
                  style="border: 1px solid #eee;"
                ></iframe>

                <!-- 保留下载按钮 -->
                <div class="pdf-download-btn">
                  <el-button type="success" icon="Download" size="large" @click="downloadFile(noticeDetail.file_path)">
                    {{ t('pages.download_pdf') }}
                  </el-button>
                </div>
              </div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import * as pdfjsLib from 'pdfjs-dist';
import { getNoticeDetailApi } from '@/assets/api/website';


const route = useRoute();
const { locale, t } = useI18n();

// 响应式数据
const loading = ref(true);
const noticeDetail = ref<any>(null);
const pdfLoading = ref(false);
const pdfError = ref(false);
const currentPage = ref(1);
const totalPages = ref(0);
const zoomLevel = ref('1');
const isFullscreen = ref(false);
const showThumbnails = ref(true);
const rotation = ref(0);

// DOM 引用
const pdfCanvasContainer = ref<HTMLDivElement | null>(null);
const canvasRefs = ref<Record<number, HTMLCanvasElement>>({});
const thumbnailRefs = ref<Record<number, HTMLCanvasElement>>({});

// PDF 实例
let pdfDoc: any = null;
let renderTasks: any[] = [];

// 计算属性
const visiblePages = ref<Array<{pageNum: number}>>([]);

// 获取公告详情
const fetchNoticeDetail = async (id: number) => {
  loading.value = true;
  try {
    const res = await getNoticeDetailApi(id, {
      lang: locale.value,
    });
    noticeDetail.value = res;

    // 如果有PDF文件，加载PDF
    // if (res.file_path) {
    //   await loadPDF(res.file_path);
    // }
  } catch (error) {
    console.error("获取公告详情失败:", error);
  } finally {
    loading.value = false;
  }
};

// 计算PDF的完整访问URL
const pdfFullUrl = computed(() => {
  // 假设 noticeDetail.file_path 是类似 '/uploads/2024/file.pdf' 的字符串
  // 直接返回即可，代理会处理路径转换
  return noticeDetail.value.file_path;
});
// 渲染指定页面
const renderPage = async (pageNum: number) => {
  if (!pdfDoc || pageNum < 1 || pageNum > totalPages.value) {
    console.error('渲染参数错误:', { pdfDoc, pageNum, totalPages: totalPages.value });
    return;
  }

  try {
    // 清理之前的渲染任务
    cancelAllRenderTasks();

    console.log(`开始渲染第 ${pageNum} 页`);

    const page = await pdfDoc.getPage(pageNum);
    const canvas = canvasRefs.value[pageNum];

    if (!canvas) {
      console.error('Canvas元素未找到');
      return;
    }

    const context = canvas.getContext('2d');
    if (!context) {
      console.error('Canvas上下文获取失败');
      return;
    }

    // 计算渲染比例
    const scale = parseFloat(zoomLevel.value);
    const viewport = page.getViewport({
      scale: scale,
      rotation: rotation.value
    });

    console.log(`页面尺寸: ${viewport.width}x${viewport.height}, 缩放: ${scale}`);

    // 设置canvas尺寸
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    // 设置容器尺寸
    if (pdfCanvasContainer.value) {
      pdfCanvasContainer.value.style.height = `${viewport.height}px`;
      pdfCanvasContainer.value.style.width = `${viewport.width}px`;
    }

    // 渲染PDF页面
    const renderContext = {
      canvasContext: context,
      viewport: viewport
    };

    const renderTask = page.render(renderContext);
    renderTasks.push(renderTask);

    await renderTask.promise;
    console.log(`第 ${pageNum} 页渲染完成`);

    currentPage.value = pageNum;
    updateVisiblePages();

  } catch (error) {
    console.error('页面渲染失败:', error);
  }
};


// 更新可见页面
const updateVisiblePages = () => {
  const pages = [];
  // 显示当前页
  pages.push({ pageNum: currentPage.value });
  visiblePages.value = pages;
};

// 监听全屏变化
const handleFullscreenChange = () => {
  isFullscreen.value = !!document.fullscreenElement;
};

// 取消所有渲染任务
const cancelAllRenderTasks = () => {
  renderTasks.forEach(task => {
    try {
      if (task && typeof task.cancel === 'function') {
        task.cancel();
      }
    } catch (err) {
      // 忽略取消错误
    }
  });
  renderTasks = [];
};

// 下载文件
const downloadFile = (path: string) => {
  const link = document.createElement('a');
  link.href = path;
  link.download = path.split('/').pop() || 'attachment.pdf';
  link.target = '_blank';
  link.rel = 'noopener noreferrer'; // 安全考虑
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// 组件卸载时的清理
const cleanup = () => {
  cancelAllRenderTasks();

  if (pdfDoc) {
    try {
      pdfDoc.destroy();
      pdfDoc = null;
    } catch (err) {
      console.error('PDF文档清理失败:', err);
    }
  }

  // 清理canvas引用
  canvasRefs.value = {};
  thumbnailRefs.value = {};
};

// 监听路由变化
watch(() => route.params.id, (newId) => {
  if (newId) {
    // 先清理之前的PDF资源
    cleanup();

    const noticeId = parseInt(newId as string);
    if (!isNaN(noticeId)) {
      fetchNoticeDetail(noticeId);
    }
  }
}, { immediate: false });

// 监听语言变化
watch(() => locale.value, () => {
  if (route.params.id) {
    const noticeId = parseInt(route.params.id as string);
    if (!isNaN(noticeId)) {
      fetchNoticeDetail(noticeId);
    }
  }
});

onMounted(() => {
  const noticeId = parseInt(route.params.id as string);
  if (!isNaN(noticeId)) {
    fetchNoticeDetail(noticeId);
  }

  // 添加全屏事件监听
  document.addEventListener('fullscreenchange', handleFullscreenChange);
});

onUnmounted(() => {
  // 清理资源
  cleanup();

  // 移除事件监听
  document.removeEventListener('fullscreenchange', handleFullscreenChange);
});
</script>

<style scoped lang="scss">
@import '@/assets/css/common.scss';

.notice-detail-page {
  .page-header {
    background-color: #f5f5f5;
    padding: 100px 0 40px;

    h1 {
      color: $primary-color;
      margin-bottom: 10px;
    }

    .breadcrumb {
      font-size: 14px;
      color: #999;

      a {
        color: #666;
        text-decoration: none;

        &:hover {
          color: $primary-color;
        }
      }

      span {
        color: $primary-color;
      }
    }
  }

  .page-content {
    padding: 50px;
  }

  .detail-wrapper {
    min-height: 600px;
    padding: 20px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  }

  .notice-article {
    .article-title {
      font-size: 28px;
      color: #333;
      text-align: center;
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom: 1px solid #eee;
    }

    .article-meta {
      display: flex;
      justify-content: center;
      gap: 20px;
      font-size: 14px;
      color: #999;
      margin-bottom: 30px;
    }

    .article-summary {
      background-color: #f9f9f9;
      padding: 15px;
      border-left: 4px solid $primary-color;
      margin-bottom: 30px;
      line-height: 1.8;
      color: #666;
    }

    .article-content {
      line-height: 1.8;
      color: #333;
      font-size: 16px;

      :deep(p) {
        margin-bottom: 15px;
      }
    }

    // PDF 相关样式
    .pdf-viewer-section {
      margin: 30px 0;
      border: 1px solid #eee;
      border-radius: 8px;
      padding: 20px;
      background: #fafafa;

      .pdf-title {
        font-size: 18px;
        color: $primary-color;
        margin-bottom: 15px;
        padding-bottom: 5px;
        border-bottom: 1px solid #eee;
      }

      .pdf-toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
        padding: 10px;
        background: white;
        border-radius: 4px;
        border: 1px solid #e4e7ed;

        .toolbar-left,
        .toolbar-right {
          display: flex;
          align-items: center;
          gap: 10px;
        }
      }

      .pdf-loading {
        min-height: 400px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .pdf-error {
        min-height: 200px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .pdf-preview-wrapper {
        position: relative;
        min-height: 500px;
        max-height: 700px;
        overflow: auto;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        transition: all 0.3s ease;

        &.fullscreen {
          max-height: none;
          height: calc(100vh - 100px);
        }

        .pdf-canvas-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 20px;
          margin: 0 auto;

          .pdf-page-canvas {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            margin-bottom: 20px;
            max-width: 100%;
            height: auto;
          }
        }

        .pdf-thumbnails {
          position: absolute;
          left: 0;
          top: 0;
          width: 120px;
          height: 100%;
          overflow-y: auto;
          background: #f5f5f5;
          border-right: 1px solid #ddd;
          padding: 10px;
          display: flex;
          flex-direction: column;
          gap: 10px;

          .thumbnail-item {
            cursor: pointer;
            padding: 5px;
            border-radius: 4px;
            border: 2px solid transparent;
            transition: all 0.2s ease;

            &:hover {
              background: #e8f4ff;
              border-color: #b3d8ff;
            }

            &.active {
              background: #d9ecff;
              border-color: $primary-color;
            }

            .thumbnail-canvas-container {
              canvas {
                width: 100%;
                height: auto;
                max-height: 150px;
                object-fit: contain;
                border: 1px solid #ddd;
                background: white;
              }
            }

            .thumbnail-page-number {
              text-align: center;
              font-size: 12px;
              color: #666;
              margin-top: 5px;
            }
          }
        }
      }

      .pdf-download-btn {
        margin-top: 20px;
        display: flex;
        justify-content: center;
        gap: 15px;
      }
    }

    .back-link {
      margin-top: 40px;
      text-align: center;
    }
  }

  // 响应式样式
  @media (max-width: 768px) {
    .article-meta {
      flex-direction: column;
      align-items: center;
      gap: 5px;
    }

    .pdf-toolbar {
      flex-direction: column;
      gap: 10px !important;

      .toolbar-left,
      .toolbar-right {
        width: 100%;
        justify-content: center;
      }
    }

    .pdf-preview-wrapper {
      .pdf-thumbnails {
        position: relative !important;
        width: 100% !important;
        height: auto !important;
        max-height: 200px;
        flex-direction: row !important;
        overflow-x: auto;
        border-right: none !important;
        border-bottom: 1px solid #ddd;

        .thumbnail-item {
          flex-shrink: 0;
          width: 80px;
        }
      }
    }

    .pdf-download-btn {
      flex-direction: column;
      align-items: center;
    }
  }

  @media (max-width: 480px) {
    .article-title {
      font-size: 22px !important;
    }

    .pdf-preview-wrapper {
      min-height: 300px !important;
    }
  }
}
</style>